import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, ArrowRight, Info, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api from "@/api";

export function Pricing() {
  const { t, money } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);

  useEffect(() => { api.get("/credits/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  const isCraftsman = user && user !== false && user.role === "craftsman";
  const cta = () => nav(isCraftsman ? "/dashboard" : "/register");

  return (
    <section id="pricing" className="relative overflow-hidden bg-[#241B17] scroll-mt-24">
      <div className="absolute inset-0 hero-grain opacity-50" />
      <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C65338]/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#E69625]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#E69625] mb-4">
            <Coins className="w-4 h-4" /> {t("credits.title")}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[0.95]">{t("pricing.title")}</h2>
          <p className="text-stone-300 mt-4 text-base">{t("pricing.subtitle")}</p>
        </div>

        <div className="mt-6 mb-12 flex items-start gap-2 text-sm text-stone-200 bg-white/5 border border-white/10 rounded-xl px-4 py-3 max-w-2xl backdrop-blur-sm">
          <Info className="w-5 h-5 text-[#E69625] shrink-0 mt-0.5" />
          <span>{t("pricing.mustBuy")}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {packages.map((p, i) => {
            const popular = p.lookup_key === "credits_25";
            const perLead = Math.round(p.price / p.credits);
            const save = Math.round((1 - perLead / 100) * 100);
            return (
              <motion.div
                key={p.lookup_key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                whileHover={{ y: -8 }}
                data-testid={`pricing-${p.lookup_key}`}
                className={`group relative rounded-3xl p-7 flex flex-col overflow-hidden ${
                  popular
                    ? "bg-gradient-to-br from-[#C65338] to-[#A93F25] text-white shadow-[0_20px_60px_-15px_rgba(198,83,56,0.7)] lg:scale-[1.05] ring-1 ring-white/20"
                    : "bg-white text-[#2D231F] shadow-xl"
                }`}
              >
                {popular && (
                  <>
                    <div className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-12 bg-white/15 blur-md"
                      style={{ animation: "promoShine 4s linear infinite" }} />
                    <span className="absolute top-5 right-5 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#C65338] text-[11px] font-black uppercase tracking-wide shadow">
                      <Sparkles className="w-3 h-3" /> {t("pricing.popular")}
                    </span>
                  </>
                )}

                <div className="relative">
                  {save > 0 && (
                    <span className={`inline-block mb-4 px-2.5 py-1 rounded-full text-[11px] font-black ${
                      popular ? "bg-white/20 text-white" : "bg-[#5C6646]/10 text-[#5C6646]"
                    }`}>
                      {t("pricing.save")} {save}%
                    </span>
                  )}
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-5xl font-black leading-none">{p.credits}</span>
                    <span className={`text-sm font-semibold ${popular ? "text-white/80" : "text-[#8C7A70]"}`}>{t("credits.leads")}</span>
                  </div>

                  <div className="mt-6 flex items-end gap-2">
                    <span className={`font-display text-3xl font-black ${popular ? "text-white" : "text-[#C65338]"}`}>{money(p.price)}</span>
                  </div>
                  <div className={`text-xs mt-1 mb-6 ${popular ? "text-white/70" : "text-[#A89A90]"}`}>
                    {money(perLead)} / lead {" · "}
                    <span className="line-through opacity-70">{money(100)}</span>
                  </div>
                </div>

                <Button
                  onClick={cta}
                  data-testid={`pricing-cta-${p.lookup_key}`}
                  className={`relative mt-auto rounded-full h-11 font-bold transition-transform group-hover:scale-[1.02] ${
                    popular
                      ? "bg-white hover:bg-white text-[#C65338]"
                      : "bg-[#2D231F] hover:bg-[#C65338] text-white"
                  }`}
                >
                  {isCraftsman ? t("pricing.gotoDash") : t("pricing.cta")}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-stone-300">
          <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#E69625]" /> {t("pricing.trust1")}</span>
          <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#E69625]" /> {t("pricing.trust2")}</span>
          <button onClick={cta} data-testid="pricing-main-cta" className="ml-auto inline-flex items-center gap-2 font-bold text-[#E69625] hover:gap-3 transition-all">
            {isCraftsman ? t("pricing.gotoDash") : t("pricing.cta")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
